import type { PageNoteView, PortalNoteView, ScreenshotType, Verdict, WidgetFeedbackParsed } from "@anota/shared";
import { SCREENSHOT_TYPES } from "@anota/shared/limits";
import { getMine, getPageNotes, mineLink, replyToMine, replyToNote, sendFeedback, sendVerdict, uploadScreenshot } from "./api";
import type { CaptureFn } from "./shot-loader";
import { markAllSeen, markSeen, mineKey, mineTokens, rememberMine, seenNotes } from "./storage";

/** La nota tal como sale del widget: sin la clave del proyecto, la captura ni el enlace de revisión, que van aparte. */
export type NoteInput = Omit<WidgetFeedbackParsed, "projectKey" | "screenshot" | "reviewToken" | "mineKey">;

/** Las notas de la página para quien revisa (pines con estado y conversación). */
export interface NotesApi {
  list: (pageUrl: string) => Promise<PageNoteView[]>;
  reply: (id: string, body: string) => Promise<void>;
  /** «¿Está bien así?» en una nota resuelta de quien mira. */
  verdict: (id: string, verdict: Verdict, body?: string) => Promise<void>;
}

/** «Tus notas»: las que dejó quien está en la página, desde este navegador o con su enlace de revisión. */
export interface MineApi {
  /** Si hay algo que enseñar (accesos guardados o enlace de revisión), sin preguntar al servidor. */
  any: () => boolean;
  list: () => Promise<PortalNoteView[]>;
  reply: (id: string, body: string) => Promise<void>;
  /** El equipo respondió o cambió el estado desde la última vez que la abrió aquí. */
  isNew: (note: PortalNoteView) => boolean;
  /** La abrió: deja de ser novedad (hasta el próximo cambio). */
  seen: (note: PortalNoteView) => void;
  /** Todas vistas: descartó el aviso o cerró «Tus notas» después de ver la lista con su estado. */
  seenAll: (notes: PortalNoteView[]) => void;
  verdict: (id: string, verdict: Verdict, body?: string) => Promise<void>;
  /** El enlace a su portal para verlas en otro dispositivo. */
  link: () => Promise<string>;
}

/** Lo que cuenta como novedad: el estado y las respuestas del equipo (las propias, no). Una nota recién enviada es "open:0". */
const signature = (n: PortalNoteView) => `${n.status}:${n.comments.filter((c) => c.authorType !== "reporter").length}`;

/**
 * A dónde va cada nota: al servidor de Anota o, con el paquete npm, a una función del propio sitio
 * (`onSubmit`), que la guarda donde quiera.
 */
export interface Backend {
  /** La captura (snapDOM) se carga solo al enviar una nota con captura. */
  loadCapture: () => Promise<CaptureFn>;
  /** Guarda la nota y su captura. Devuelve el número de la nota, si lo hay. */
  submit: (note: NoteInput, screenshot: Blob | null) => Promise<{ number?: number }>;
  /** Solo con servidor de Anota y enlace de revisión: ver y responder las notas de la página. */
  notes?: NotesApi;
  /** Solo con servidor de Anota: el historial de quien anota, con el estado y las respuestas. */
  mine?: MineApi;
}

/** El servidor de Anota: primero la nota (JSON) y después, si la hay, la captura con su token de un solo uso. */
export function serverBackend(base: string, projectKey: string, loadCapture: () => Promise<CaptureFn>, reviewToken?: string): Backend {
  /** Con qué entra a una de sus notas: el acceso que guardó este navegador y, si lo hay, su enlace de revisión. */
  const auth = (id: string) => {
    const token = mineTokens(projectKey).find((t) => t.startsWith(`${id}.`));
    return { ...(token ? { token } : {}), ...(reviewToken ? { reviewToken } : {}) };
  };
  return {
    loadCapture,
    async submit(note, shot) {
      const type = shot && (SCREENSHOT_TYPES as readonly string[]).includes(shot.type) ? (shot.type as ScreenshotType) : null;
      const created = await sendFeedback(base, {
        projectKey,
        ...note,
        mineKey: mineKey(projectKey),
        ...(reviewToken ? { reviewToken } : {}),
        ...(shot && type ? { screenshot: { contentType: type, bytes: shot.size } } : {}),
      });
      // Para «Tus notas»: el acceso a esta nota queda en el navegador.
      if (created.access) rememberMine(projectKey, created.access);
      if (shot && type && created.upload) await uploadScreenshot(created.upload, shot).catch((err: unknown) => console.warn("[Anota]", err));
      return { number: created.number };
    },
    mine: {
      any: () => Boolean(reviewToken) || mineTokens(projectKey).length > 0,
      list: async () => (await getMine(base, projectKey, mineTokens(projectKey), reviewToken)).items,
      reply: async (id, body) => {
        await replyToMine(base, projectKey, id, auth(id), body);
      },
      verdict: async (id, verdict, body) => {
        await sendVerdict(base, "mine", id, { projectKey, ...auth(id), verdict, ...(body ? { body } : {}) });
      },
      link: async () => (await mineLink(base, projectKey, mineKey(projectKey), mineTokens(projectKey))).url,
      isNew: (n) => (seenNotes(projectKey)[n.id] ?? "open:0") !== signature(n),
      seen: (n) => markSeen(projectKey, n.id, signature(n)),
      seenAll: (notes) =>
        markAllSeen(
          projectKey,
          notes.map((n) => [n.id, signature(n)])
        ),
    },
    ...(reviewToken
      ? {
          notes: {
            list: async (pageUrl: string) => (await getPageNotes(base, projectKey, reviewToken, pageUrl)).items,
            reply: async (id: string, body: string) => {
              await replyToNote(base, projectKey, reviewToken, id, body);
            },
            verdict: async (id: string, verdict: Verdict, body?: string) => {
              await sendVerdict(base, "notes", id, { projectKey, reviewToken, verdict, ...(body ? { body } : {}) });
            },
          },
        }
      : {}),
  };
}
